/**
 * useAdminEntryAdapter — «внесение за сотрудника» реализация `EntryEditorAdapter`
 * (задача 77 §1.1).
 *
 * Поверх admin-timesheet-хуков (`useCreate/Replace/DeleteAdminEntry`), которые
 * уже инвалидируют snapshot выбранного сотрудника/даты. БЕЗ offline-очереди и
 * БЕЗ 409-спец-кейса (руководитель онлайн за столом). Финал (sign/approve)
 * принадлежит дневной оболочке (`EntryDock` / `AdminTimesheetBackfillPage`), не
 * редактору — поэтому адаптер пишет только записи.
 *
 * Потребители (§3.1 `EntryDock`, §4.1 backfill-страница) подключаются поверх
 * этого адаптера; здесь — surface-агностичная часть контракта.
 */
import { useTranslation } from "react-i18next";

import type { TimesheetEntry } from "@/api/admin-timesheet";
import {
  useCreateAdminEntry,
  useDeleteAdminEntry,
  useReplaceAdminEntry,
} from "@/hooks/useAdminTimesheet";

import type { EntryEditorAdapter } from "./types";
import { apiErrorFormIssue } from "@/components/ui/form-validation";
import { showApiErrorToast } from "@/lib/api-error-toast";

export interface AdminEntryAdapterOptions {
  userId: number;
  date: string;
  /** Закрыть редактор после успешной мутации. */
  onClose: () => void;
  /** После успешного create/replace с новой entry. */
  onSaved: (entry: TimesheetEntry) => void;
  /** После успешного delete. */
  onDeleted?: () => void;
}

export function useAdminEntryAdapter({
  userId,
  date,
  onClose,
  onSaved,
  onDeleted,
}: AdminEntryAdapterOptions): EntryEditorAdapter {
  const { t } = useTranslation();
  const createMut = useCreateAdminEntry();
  const replaceMut = useReplaceAdminEntry();
  const deleteMut = useDeleteAdminEntry();

  function onError(err: unknown): void {
    showApiErrorToast(err, { fallbackMessage: t("common.apiError.fallback") });
  }

  return {
    create: (payload) =>
      createMut.mutate({ userId, date, payload }, { onSuccess: onSaved, onError }),
    replace: (entryId, payload) =>
      replaceMut.mutate({ userId, date, entryId, payload }, { onSuccess: onSaved, onError }),
    remove: async (entryId) => {
      try {
        await deleteMut.mutateAsync({ userId, date, entryId });
        onDeleted?.();
        onClose();
      } catch (error) {
        onError(error as Error);
      }
    },
    isSaving: createMut.isPending || replaceMut.isPending,
    isDeleting: deleteMut.isPending,
    saveError:
      (createMut.error ?? replaceMut.error)
        ? apiErrorFormIssue(createMut.error ?? replaceMut.error, {
            id: "field-entry-flow-server-error",
            fallbackMessage: t("common.apiError.fallback"),
          })
        : null,
  };
}
